import { computed, ref } from "vue";
import { HttpError, request, setPartnerTokenProvider } from "@/shared/http/client";
import type { PartnerIdentity, PartnerImpersonation } from "./partner.types";

/**
 * The portal's session — its OWN, and nothing it touches is the panel's.
 *
 * One storage key, one token provider, and every request below passes
 * `auth: 'partner'` by hand. V2 kept both worlds on the same JWT secret,
 * separated by a literal `type:'affiliate'` inside the payload, and signing the
 * affiliate out ran `localStorage.clear()` — which took the panel's session with
 * it. Neither can happen here: the two never share a key, a provider or a header.
 *
 * The ACCESS token stays in memory and only the refresh token is written down,
 * for the same reason the panel keeps its token in a ref: this app renders
 * markdown written by strangers.
 */
const STORAGE_KEY = "benvox.partner.session";

interface Stored {
	readonly refreshToken?: string;
	/** Present only on a BORROWED look, which has no refresh token by design. */
	readonly accessToken?: string;
	readonly expiresAt?: string;
	readonly borrowedFrom?: string;
	readonly reason?: string;
}

const accessToken = ref<string | null>(null);
const partner = ref<PartnerIdentity | null>(null);
const borrowed = ref<{ name: string; reason: string; until: string } | null>(null);

// Registered at module load, like the panel's: from here the client attaches the
// partner token itself and no screen has to think about it.
setPartnerTokenProvider(() => accessToken.value);

function read(): Stored | null {
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		return raw ? (JSON.parse(raw) as Stored) : null;
	} catch {
		return null;
	}
}

/** Only OUR key, ever. Never `localStorage.clear()`. */
function write(value: Stored | null): void {
	try {
		if (value) localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
		else localStorage.removeItem(STORAGE_KEY);
	} catch {
		// Storage denied still works for the length of one visit.
	}
}

interface SessionAnswer {
	readonly accessToken: string;
	readonly refreshToken: string;
	readonly partner: PartnerIdentity;
}

function adopt(answer: SessionAnswer): void {
	accessToken.value = answer.accessToken;
	partner.value = answer.partner;
	borrowed.value = null;
	write({ refreshToken: answer.refreshToken });
}

function forget(): void {
	accessToken.value = null;
	partner.value = null;
	borrowed.value = null;
	write(null);
}

export function usePartnerSession() {
	return { partner: computed(() => partner.value), borrowed: computed(() => borrowed.value) };
}

export async function partnerSignIn(email: string, password: string): Promise<void> {
	adopt(
		await request<SessionAnswer>("/v3/partner/session", {
			method: "POST",
			body: { email, password },
			auth: "none",
		}),
	);
}

export async function partnerSignOut(): Promise<void> {
	try {
		await request("/v3/partner/session", { method: "DELETE", auth: "partner" });
	} catch {
		// Leaving locally must succeed even when the server call does not.
	}
	forget();
}

/** Both of these run signed OUT, so neither may carry a token of either kind. */
export function partnerForgotPassword(email: string): Promise<void> {
	return request("/v3/partner/password/forgot", { method: "POST", body: { email }, auth: "none" });
}

export function partnerSetPassword(token: string, password: string): Promise<void> {
	return request("/v3/partner/password", {
		method: "POST",
		body: { token, password },
		auth: "none",
	});
}

/**
 * A grant the admin was given, adopted so the portal renders it unchanged.
 *
 * No refresh token: when it expires it is simply over, which is the difference
 * between a borrowed look and the seven-day copy of a real login V2 handed out.
 */
export function adoptImpersonation(grant: PartnerImpersonation): void {
	write({
		accessToken: grant.accessToken,
		expiresAt: grant.expiresAt,
		borrowedFrom: grant.partnerName,
		reason: grant.reason,
	});
}

let pending: Promise<boolean> | null = null;

/** Whether there is a usable portal session, restoring one if there is a way. */
export function ensurePartnerSession(): Promise<boolean> {
	if (accessToken.value) return Promise.resolve(true);
	pending ??= restore().finally(() => {
		pending = null;
	});
	return pending;
}

async function restore(): Promise<boolean> {
	const stored = read();
	if (!stored) return false;

	if (stored.accessToken && stored.expiresAt && Date.parse(stored.expiresAt) > Date.now()) {
		accessToken.value = stored.accessToken;
		borrowed.value = {
			name: stored.borrowedFrom ?? "parceiro",
			reason: stored.reason ?? "",
			until: stored.expiresAt,
		};
		return await loadMe();
	}
	if (!stored.refreshToken) {
		write(null);
		return false;
	}

	try {
		adopt(
			await request<SessionAnswer>("/v3/partner/session/refresh", {
				method: "POST",
				body: { refreshToken: stored.refreshToken },
				auth: "none",
			}),
		);
		return true;
	} catch {
		write(null);
		return false;
	}
}

async function loadMe(): Promise<boolean> {
	try {
		partner.value = await request<PartnerIdentity>("/v3/partner/me", { auth: "partner" });
		return true;
	} catch {
		forget();
		return false;
	}
}

/** What `to` has to look like. Structural, so a `RouteLocationNormalized` fits. */
export interface PartnerRouteTarget {
	readonly fullPath: string;
	readonly path: string;
	readonly name?: unknown;
	readonly meta?: unknown;
}

/**
 * The router's answer for a partner route, called BEFORE the panel's own restore
 * — so a tenant session is never restored on a partner address, and the reverse.
 *
 * The sign-in screen answers `true` even with no session, and it must: the guard
 * runs on every partner route including that one, and sending "no session" to
 * the screen that fixes it is an infinite redirect.
 *
 * O critério é `public: true` na meta e não uma lista de nomes. `/parceiro/senha`
 * — o endereço que o convite e o "esqueci a senha" imprimem no e-mail — cai
 * exatamente no mesmo caso: quem chega com o token AINDA não tem sessão, e a
 * lista teria de ser lembrada rota a rota. Mandá-lo para o login levava o token
 * enterrado dentro de `redirect`, onde a tela não o lê: o parceiro convidado
 * ficava sem nenhum caminho para definir a própria senha.
 */
export async function partnerGuardResult(
	to: PartnerRouteTarget,
): Promise<true | { name: string; query?: Record<string, string> }> {
	if ((to.meta as { public?: unknown } | undefined)?.public === true) return true;
	if (to.name === "partner-sign-in" || to.path === "/parceiro/entrar") return true;
	if (await ensurePartnerSession()) return true;
	return { name: "partner-sign-in", query: { redirect: to.fullPath } };
}

/**
 * POR QUE um link de senha não valeu — o código que o servidor nomeia, e não o
 * texto dele.
 *
 * As duas recusas de `POST /v3/partner/password` chegam como 400 e são coisas
 * opostas para quem está na tela: "curta demais" se resolve digitando outra, e
 * "link gasto" só se resolve pedindo outro. A tela mostrava a mesma frase de
 * falha genérica para ambas — o piso de 12 caracteres do servidor devolvia
 * "não deu para concluir agora", que não diz o que fazer.
 */
export function partnerPasswordFailure(e: unknown): "weak" | "dead" | null {
	if (!(e instanceof HttpError) || e.status !== 400) return null;
	const code = (e.payload as { error?: unknown } | undefined)?.error;
	if (code === "WEAK_PASSWORD") return "weak";
	if (code === "INVALID_TOKEN") return "dead";
	return null;
}

export function isPartnerUnauthorized(e: unknown): boolean {
	return e instanceof HttpError && e.kind === "unauthorized";
}

/**
 * O login recusado porque a conta está em REDEFINIÇÃO FORÇADA (0191 do servidor).
 *
 * É a única recusa desta rota que devolve algo: um token de reset novo, e só
 * chega a ela quem digitou a senha CERTA. Quem errou continua recebendo o 401 de
 * sempre — a bandeira não vaza para quem não tem a senha.
 *
 * O token vem do SERVIDOR e não é remontado aqui: o portal navega para
 * `/parceiro/senha?token=…`, que é o mesmo endereço que o e-mail imprime.
 * Concatenar a rota num `.vue` seria uma segunda cópia do link para divergir.
 */
export function partnerResetRequired(e: unknown): string | null {
	if (!(e instanceof HttpError) || e.status !== 409) return null;
	const payload = e.payload as { error?: unknown; resetToken?: unknown } | undefined;
	if (payload?.error !== "PASSWORD_RESET_REQUIRED") return null;
	return typeof payload.resetToken === "string" && payload.resetToken ? payload.resetToken : null;
}
